import i18n from '@translations';
import * as Yup from 'yup';

import { TimeoutAdornment } from '@components';
import { TIMEOUT_LIMITS } from '@constants';
import {
  ApiEndpoint,
  columnDirection,
  columnSpace,
  ConditionOperatorType,
  ConditionVariableType,
  FormActionType,
  FormType,
  InputType,
  ServiceModalName,
  Translation,
} from '@enums';
import type { Condition, EndpointType, FormConfig, FormConfigMapping } from '@types';
import { optionType } from '@utils';

interface GetRouteFormConfigParams {
  endpoint?: EndpointType;
  originOptionList?: optionType[];
  schema: Yup.AnySchema;
  isEdit?: boolean;
}

export const getRouteFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  originOptionList = [],
  schema,
  isEdit = false,
}: GetRouteFormConfigParams): FormConfig => {
  const tRoute: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:forms.route.${key}`);

  const emptyData: { main: Condition } = {
    main: {
      field: ConditionVariableType.URI,
      argument: '',
      operator: ConditionOperatorType.stringEqual,
      values: [{ value: '' }],
    },
  };

  const getActionTranslation = (baseKey: string): string => {
    const action = isEdit ? 'edit' : 'add';
    return i18n.t(
      `${Translation.Configuration}:${baseKey}.${action}${baseKey.includes('modal') ? 'ApplicationRoute' : 'Route'}`,
    );
  };

  const ROUTE_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.CdnRoute,
      endpoint: ApiEndpoint.DEFAULT,
      formActionType: isEdit ? FormActionType.Edit : FormActionType.Create,
      modalName: ServiceModalName.FormModal,
      label: {
        title: getActionTranslation('modals.route'),
        description: i18n.t(
          `${Translation.Configuration}:modals.route.${isEdit ? 'editRouteDescription' : 'addNewRouteDescription'}`,
        ),
        confirmButton: i18n.t(`${Translation.Configuration}:buttons.route.${isEdit ? 'editRoute' : 'addRoute'}`),
      },
      inputList: [
        {
          name: 'name',
          defaultValue: '',
          inputType: InputType.Text,
          placeholder: tRoute('enterYourRulesName'),
          label: tRoute('rulesName'),
          direction: columnDirection.Row,
          required: true,
        },
        {
          name: 'conditions',
          defaultValue: '',
          inputType: InputType.Table,
          placeholder: '',
          direction: columnDirection.Column,
          columnPercentage: columnSpace.TwoOverFive,
          format: tRoute,
          withoutLabel: true,
          fullWidth: true,
          required: false,
          emptyData,
        },
        {
          name: 'origin',
          defaultValue: '',
          inputType: InputType.Select,
          placeholder: tRoute('selectYourOriginServer'),
          label: tRoute('originServer'),
          direction: columnDirection.Row,
          options: originOptionList,
          required: true,
        },
        {
          name: 'connect_timeout',
          defaultValue: TIMEOUT_LIMITS.DEFAULT_TIMEOUT,
          type: 'number' as const,
          placeholder: '1',
          label: i18n.t(`${Translation.Configuration}:forms.route.connectionTimeOut`),
          direction: columnDirection.Row,
          adornment: TimeoutAdornment(`${Translation.Common}:tables.seconds`),
          adornmentClassName: 'pr-3',
          min: TIMEOUT_LIMITS.MIN_CONNECTION_SEND,
          max: TIMEOUT_LIMITS.MAX_CONNECTION_SEND,
          required: false,
        },
        {
          name: 'send_timeout',
          defaultValue: TIMEOUT_LIMITS.DEFAULT_TIMEOUT,
          type: 'number' as const,
          placeholder: '1',
          label: i18n.t(`${Translation.Configuration}:forms.route.sendTimeOut`),
          direction: columnDirection.Row,
          adornment: TimeoutAdornment(`${Translation.Common}:tables.seconds`),
          adornmentClassName: 'pr-3',
          min: TIMEOUT_LIMITS.MIN_CONNECTION_SEND,
          max: TIMEOUT_LIMITS.MAX_CONNECTION_SEND,
          required: false,
        },
        {
          name: 'read_timeout',
          defaultValue: TIMEOUT_LIMITS.DEFAULT_TIMEOUT,
          type: 'number' as const,
          placeholder: '1',
          label: i18n.t(`${Translation.Configuration}:forms.route.readTimeOut`),
          direction: columnDirection.Row,
          adornment: TimeoutAdornment(`${Translation.Common}:tables.seconds`),
          adornmentClassName: 'pr-3',
          min: TIMEOUT_LIMITS.MIN_READ,
          max: TIMEOUT_LIMITS.MAX_READ,
          required: false,
        },
      ],
      validationSchema: schema,
    },
  };

  return (ROUTE_FORM[endpoint] ?? ROUTE_FORM[ApiEndpoint.DEFAULT]) as FormConfig;
};
